import { Box, Stack, Typography } from '@mui/material';
import { forwardRef, useImperativeHandle, useState } from 'react';

import {
  FilterContentContainer,
  FilterItem,
  Header,
  SelectAllContainer,
  StyledCollapse,
  Wrapper,
} from './styles';
import {
  FiltersOptions,
  ICheckBoxFilterProps,
  IFormatCheckedOptions,
} from './types';
import Icon from '../../Icon';
import newUseFilterParams from '../../../hooks/newUseFilterParams';
import { FilterButton } from '../../FiltersList/styles';
import { SimpleSearchField } from './components/SimpleSearchField';
import { FilterLoading } from './components/FilterLoading';
import InfiniteScroll from 'react-infinite-scroll-component';
import CheckboxAllSelected from './components/CheckboxAllSelected';
import ResultListCheck from './components/ResultListCheck';
import { NEW_COLORS } from '../../../themes/colors/newColors';

const CheckboxFilter = forwardRef(
  (
    {
      filters,
      isMultiple = true,
      searchFieldProps,
      loadNextPage,
      title,
      disabledSelectAll,
      name,
      isLoading,
    }: ICheckBoxFilterProps,
    ref,
  ) => {
    const [openState, setOpenState] = useState<boolean>(false);

    const [allSelectedCheckState, setAllSelectedCheckState] =
      useState<boolean>(false);

    const hasSearchField = Boolean(searchFieldProps);

    const dynamicPage = Boolean(loadNextPage);

    const isEmptyDynamicPage =
      dynamicPage && loadNextPage?.data?.pages[0] === undefined;

    const showSelectAll = !disabledSelectAll && isMultiple;

    const { params, changeFilter } = newUseFilterParams({
      filters: {
        [name]: '',
      },
    });

    const filtersIsEmpty = filters.length === 0;

    const generateIOptionItem = (value: FiltersOptions) => {
      const state = {
        value: value?.value,
        label: value?.label,
        content: value?.content,
      };

      return state;
    };

    const filterOptionListCheck = filters?.filter(item =>
      params[name]?.split(',').includes(item?.value?.toString()),
    );

    const formatedList = {
      [name]: filterOptionListCheck,
    };

    const [selectedFilterState, setSelectedFilterValues] =
      useState<IFormatCheckedOptions>(formatedList);

    const handleChange = (option: FiltersOptions) => {
      const selectedValues = selectedFilterState[name] || [];

      const isAlreadyExistsOption = selectedValues.some(
        item => item?.value === option?.value,
      );

      const updatedSelectedValues = isAlreadyExistsOption
        ? selectedValues.filter(item => item?.value !== option?.value)
        : [...selectedValues, generateIOptionItem(option)];

      setSelectedFilterValues(prevState => ({
        ...prevState,
        [name]: updatedSelectedValues,
      }));

      setAllSelectedCheckState(updatedSelectedValues.length === filters.length);
    };

    const handleCheckedOption = (option: FiltersOptions) => {
      return selectedFilterState[name]?.some(
        item => item?.value === option?.value,
      );
    };

    const handleSubmitFilter = () => {
      changeFilter(
        name,
        String(selectedFilterState[name]?.map(item => item?.value).join(',')),
      );
    };

    const handleSelectAll = () => {
      if (allSelectedCheckState) {
        setSelectedFilterValues(prevState => ({
          ...prevState,
          [name]: [],
        }));
      } else {
        const allOptions = filters.map(option => generateIOptionItem(option));
        setSelectedFilterValues(prevState => ({
          ...prevState,
          [name]: allOptions,
        }));
      }
      setAllSelectedCheckState(!allSelectedCheckState);
    };

    useImperativeHandle(ref, () => ({
      handleSubmitFilter,
      filterValue: {
        [name]: String(
          selectedFilterState[name]?.map(item => item?.value).join(','),
        ),
      },
    }));

    const messageCheckBox = () => {
      if (allSelectedCheckState) {
        return 'Todos selecionados';
      }
      if (isEmptyDynamicPage) {
        return 'Nenhum filtro disponível';
      }
      return 'Nenhum selecionado';
    };

    return (
      <Wrapper
        disabled={filtersIsEmpty || isLoading}
        bgcolor={openState ? NEW_COLORS.BASE.WHITE : NEW_COLORS.GRAYSCALE[50]}
      >
        <Header
          onClick={() => {
            setOpenState(!openState);
          }}
        >
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="text">{title}</Typography>
            <Icon
              name={
                openState
                  ? 'actions-b2b-chevron-up'
                  : 'actions-b2b-chevron-down'
              }
              size={24}
            />
          </Stack>

          {selectedFilterState[name]?.length !== 0 && !allSelectedCheckState ? (
            <Box flexWrap="wrap" display="flex" gap={1}>
              {selectedFilterState[name]?.map(filter => (
                <div key={String(filter.value)}>
                  <FilterButton mt={1}>
                    <Typography>{filter?.label}</Typography>
                    <Icon
                      onClick={e => {
                        e.stopPropagation();
                        handleChange(filter);
                      }}
                      name="actions-b2b-close"
                      size={16}
                      color={NEW_COLORS.BASE.WHITE}
                    />
                  </FilterButton>
                </div>
              ))}
            </Box>
          ) : (
            <Typography variant="smallText">
              {filters?.length === 0
                ? 'Nenhum filtro disponível'
                : messageCheckBox()}
            </Typography>
          )}
        </Header>

        <StyledCollapse in={openState}>
          <>
            {hasSearchField && dynamicPage && (
              <SimpleSearchField {...searchFieldProps} />
            )}

            <>
              {showSelectAll && (
                <SelectAllContainer>
                  <CheckboxAllSelected
                    allSelectedCheckbox={allSelectedCheckState}
                    handleSelectAll={handleSelectAll}
                    isDynamicPageEmpty={isEmptyDynamicPage}
                  />
                </SelectAllContainer>
              )}
              <FilterContentContainer>
                {dynamicPage ? (
                  <InfiniteScroll
                    dataLength={filters?.length}
                    hasMore={loadNextPage.hasNextPage}
                    next={loadNextPage.handleNextPage}
                    loader={<></>}
                    height={265}
                  >
                    {filters?.map(option => (
                      <FilterItem
                        onClick={() => handleChange(option)}
                        key={String(option.value)}
                      >
                        <>
                          {loadNextPage?.data?.pages[0] !== undefined && (
                            <ResultListCheck
                              option={option}
                              handleChange={handleChange}
                              handleCheckedOption={handleCheckedOption}
                            />
                          )}
                        </>
                      </FilterItem>
                    ))}
                  </InfiniteScroll>
                ) : (
                  <>
                    {filters?.map(option => (
                      <FilterItem
                        onClick={() => handleChange(option)}
                        key={String(option.value)}
                      >
                        <ResultListCheck
                          option={option}
                          handleChange={handleChange}
                          handleCheckedOption={handleCheckedOption}
                        />
                      </FilterItem>
                    ))}
                  </>
                )}
              </FilterContentContainer>
            </>
          </>
        </StyledCollapse>

        {loadNextPage?.isLoading && <FilterLoading />}
      </Wrapper>
    );
  },
);

export default CheckboxFilter;
